import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import {
  getCrmIntegrations,
  getDocumentEditingIntegrations,
  getEsignatureIntegrations,
  getProcurementIntegrations,
  getStorageIntegrations,
} from '~/common/constants/integrations';
import { CrmType } from '~/enums/CrmType';
import { EsignatureType } from '~/enums/EsignatureType';
import { useESignatureIntegrationData } from '~/features/admin/ESignature/utils/ESignatureIntegrations/useESignatureIntegration';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { ESignatureIntegration } from '../admin/ESignature/types';

export const useSyncPairs = (clientId?: string) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();

  const enableWebhooksIntegration = useFlag(FlagType.Webhooks);
  const isESignatureV2Enabled = useFlag(FlagType.EsignatureV2Revamp);

  const { data: storageSyncPairs = [], isLoading: isLoadingStorageSyncPairs } =
    api.endpoints.getSyncPairs.useQuery(
      { clientId: clientId as string },
      { skip: !clientId },
    );

  const { data: aribaSyncPairs = [], isLoading: isLoadingAribaSyncPairs } =
    api.endpoints.getSyncPairsAriba.useQuery(
      { clientId: clientId as string },
      { skip: !clientId },
    );

  const { data: crmConnections = [], isLoading: isLoadingCrmConnections } =
    api.endpoints.getCrmConnections.useQuery(
      { clientId: clientId as string },
      { skip: !clientId },
    );

  const transformedCrmConnections = (crmConnections || []).map(
    (connection) => ({
      ...connection,
      provider: CrmType.Salesforce,
      id: connection.id || connection.orgId,
    }),
  );

  const {
    data: endpointsData = { results: [], total: 0 },
    isLoading: isLoadingWebhooksIntegration,
  } = api.endpoints.getEndpoints.useQuery(
    {},
    { skip: !enableWebhooksIntegration },
  );

  const {
    integrationsMap,
    isLoading: isLoadingEsignatureIntegration,
    error,
    refetch: _refetch,
  } = useESignatureIntegrationData(currentUser, isESignatureV2Enabled);

  const integrationsPairs = Object.values(integrationsMap);

  const esignatureIntegrations = getEsignatureIntegrations();
  const transformedIntegrationsPairs = integrationsPairs.map((integration) => {
    return {
      ...integration,
      ...((integration as ESignatureIntegration)?.providerName
        ? esignatureIntegrations[
            (integration as ESignatureIntegration)
              .providerName as EsignatureType
          ]
        : {}),
    };
  });

  const activeEsignatureIntegrationCount = integrationsPairs.filter(
    (integration: any) =>
      (integration as ESignatureIntegration).id &&
      !(integration as ESignatureIntegration).id.startsWith('mock-'),
  ).length;

  const eSignatureIntegrationErrorMessage =
    error &&
    intl.formatMessage({
      id: 'ESignatureIntegrations.fetchError',
      defaultMessage:
        'Unable to fetch third party integrations, please try again.',
      description: 'Error message when integrations fail to load',
    });

  const allSyncPairsArray = [
    ...(storageSyncPairs || []),
    ...(aribaSyncPairs || []),
    ...transformedCrmConnections,
    ...(transformedIntegrationsPairs || []),
  ];

  useEffect(() => {
    if (allSyncPairsArray.length > 0) {
      dispatch(slices.integrations.actions.setSyncPairs(allSyncPairsArray));
    }
  }, [storageSyncPairs, aribaSyncPairs, crmConnections, integrationsMap]);

  const syncPairs = allSyncPairsArray.map((syncPair: any) => {
    const storageIntegrations = getStorageIntegrations();
    const procurementIntegrations = getProcurementIntegrations();
    const documentEditingIntegrations = getDocumentEditingIntegrations();
    const crmIntegrations = getCrmIntegrations();

    return {
      ...((storageIntegrations as any)[syncPair.provider] || {}),
      ...((procurementIntegrations as any)[syncPair.provider] || {}),
      ...((documentEditingIntegrations as any)[syncPair.provider] || {}),
      ...((crmIntegrations as any)[syncPair.provider] || {}),
      ...syncPair,
    };
  });

  return {
    syncPairs,
    endpointsData,
    isLoadingStorageSyncPairs,
    isLoadingAribaSyncPairs,
    isLoadingCrmConnections,
    isLoadingEsignatureIntegration,
    _isLoadingWebhooksIntegration: isLoadingWebhooksIntegration,
    _eSignatureIntegrationErrorMessage: eSignatureIntegrationErrorMessage,
    _activeEsignatureIntegrationCount: activeEsignatureIntegrationCount,
    esignIntegrations: transformedIntegrationsPairs,
  };
};
